import QRCode from 'qrcode';
import type { QrLabelText } from './qr-label-text.interface';

const LABEL_MARGIN_PX = 4;
const MIN_QR_SIZE_PX = 48;
const MIN_TEXT_FONT_PX = 14;
const MAX_TYPE_FONT_PX = 44;
const MIN_TYPE_FONT_PX = 18;
const MAX_CAPTION_FONT_PX = 32;
const TEXT_LINE_HEIGHT_RATIO = 1.12;
const MAX_TYPE_HEIGHT_RATIO = 0.24;
const MAX_CAPTION_HEIGHT_RATIO = 0.26;

export function wrapCaptionLines(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
): string[] {
  const words = text.split(/\s+/).filter(Boolean);
  if (words.length === 0) {
    return [];
  }

  const lines: string[] = [];
  let current = '';

  const pushCurrent = (): void => {
    if (current) {
      lines.push(current);
      current = '';
    }
  };

  const breakLongToken = (token: string): string[] => {
    const parts: string[] = [];
    let part = '';
    for (const char of [...token]) {
      const candidate = part + char;
      if (ctx.measureText(candidate).width <= maxWidth) {
        part = candidate;
      } else {
        if (part) {
          parts.push(part);
        }
        part = char;
      }
    }
    if (part) {
      parts.push(part);
    }
    return parts.length > 0 ? parts : [token];
  };

  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word;
    if (ctx.measureText(candidate).width <= maxWidth) {
      current = candidate;
      continue;
    }

    pushCurrent();

    if (ctx.measureText(word).width <= maxWidth) {
      current = word;
      continue;
    }

    const parts = breakLongToken(word);
    for (let i = 0; i < parts.length - 1; i++) {
      const part = parts[i];
      if (part) {
        lines.push(part);
      }
    }
    current = parts.at(-1) ?? '';
  }

  pushCurrent();
  return lines;
}

interface TextBlockLayout {
  fontSize: number;
  lines: string[];
  blockHeight: number;
}

function layoutTextBlock(
  ctx: CanvasRenderingContext2D,
  text: string,
  layoutWidth: number,
  maxBlockHeight: number,
  options: {
    maxFontPx: number;
    minFontPx: number;
    fontSizeDivisor: number;
    minFontFloor: number;
  },
): TextBlockLayout {
  const maxTextWidth = layoutWidth - LABEL_MARGIN_PX * 2;
  let fontSize = Math.min(
    options.maxFontPx,
    Math.max(options.minFontFloor, Math.floor(layoutWidth / options.fontSizeDivisor)),
  );
  let lines: string[] = [];
  let blockHeight = 0;

  while (fontSize >= options.minFontPx) {
    ctx.font = `700 ${fontSize}px sans-serif`;
    lines = wrapCaptionLines(ctx, text, maxTextWidth);
    const lineHeight = fontSize * TEXT_LINE_HEIGHT_RATIO;
    blockHeight = lines.length * lineHeight;
    if (blockHeight <= maxBlockHeight || fontSize === options.minFontPx) {
      break;
    }
    fontSize -= 1;
  }

  return { fontSize, lines, blockHeight };
}

function layoutTypeLabel(
  ctx: CanvasRenderingContext2D,
  typeText: string,
  layoutWidth: number,
  layoutHeight: number,
): TextBlockLayout {
  const maxBlockHeight = Math.floor(layoutHeight * MAX_TYPE_HEIGHT_RATIO);
  return layoutTextBlock(ctx, typeText, layoutWidth, maxBlockHeight, {
    maxFontPx: MAX_TYPE_FONT_PX,
    minFontPx: MIN_TYPE_FONT_PX,
    fontSizeDivisor: 3.5,
    minFontFloor: 22,
  });
}

function layoutCaption(
  ctx: CanvasRenderingContext2D,
  captionText: string,
  layoutWidth: number,
  layoutHeight: number,
): TextBlockLayout {
  const maxBlockHeight = Math.floor(layoutHeight * MAX_CAPTION_HEIGHT_RATIO);
  return layoutTextBlock(ctx, captionText, layoutWidth, maxBlockHeight, {
    maxFontPx: MAX_CAPTION_FONT_PX,
    minFontPx: MIN_TEXT_FONT_PX,
    fontSizeDivisor: 5,
    minFontFloor: 18,
  });
}

/**
 * Renders a QR label rotated 90° clockwise.
 * Layout (top → bottom): entity type, QR, optional caption (e.g. surname).
 */
export async function renderRotatedQrLabelCanvas(
  canvas: HTMLCanvasElement,
  qrPayload: string,
  label?: QrLabelText,
): Promise<void> {
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('CANVAS_CONTEXT');
  }

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  const typeText = label?.typeLabel?.trim() ?? '';
  const captionText = label?.caption?.trim() ?? '';
  const layoutWidth = canvas.height;
  const layoutHeight = canvas.width;

  let typeLayout: TextBlockLayout | null = null;
  if (typeText) {
    typeLayout = layoutTypeLabel(ctx, typeText, layoutWidth, layoutHeight);
  }

  let captionLayout: TextBlockLayout | null = null;
  if (captionText) {
    captionLayout = layoutCaption(ctx, captionText, layoutWidth, layoutHeight);
  }

  const typeBlockHeight = typeLayout?.blockHeight ?? 0;
  const captionBlockHeight = captionLayout?.blockHeight ?? 0;
  const verticalGaps =
    LABEL_MARGIN_PX *
    ((typeText ? 1 : 0) + (captionText ? 1 : 0) + 1);
  const qrAreaHeight =
    layoutHeight - typeBlockHeight - captionBlockHeight - verticalGaps;
  const qrSize = Math.max(
    MIN_QR_SIZE_PX,
    Math.min(layoutWidth - LABEL_MARGIN_PX * 2, qrAreaHeight),
  );

  const qrCanvas = document.createElement('canvas');
  await QRCode.toCanvas(qrCanvas, qrPayload, {
    width: qrSize,
    margin: 1,
    color: { dark: '#000000', light: '#ffffff' },
  });

  ctx.save();
  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate(Math.PI / 2);

  const layoutTop = -layoutHeight / 2;
  let cursorY = layoutTop + LABEL_MARGIN_PX;

  if (typeLayout && typeLayout.lines.length > 0) {
    const { fontSize, lines } = typeLayout;
    const lineHeight = fontSize * TEXT_LINE_HEIGHT_RATIO;
    ctx.fillStyle = '#000000';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.font = `700 ${fontSize}px sans-serif`;
    for (const line of lines) {
      ctx.fillText(line, 0, cursorY);
      cursorY += lineHeight;
    }
    cursorY += LABEL_MARGIN_PX;
  }

  const qrY = cursorY + Math.max(0, (qrAreaHeight - qrSize) / 2);
  ctx.drawImage(qrCanvas, -qrSize / 2, qrY, qrSize, qrSize);
  cursorY = qrY + qrSize + LABEL_MARGIN_PX;

  if (captionLayout && captionLayout.lines.length > 0) {
    const { fontSize, lines } = captionLayout;
    const lineHeight = fontSize * TEXT_LINE_HEIGHT_RATIO;
    ctx.fillStyle = '#000000';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.font = `700 ${fontSize}px sans-serif`;
    for (const line of lines) {
      ctx.fillText(line, 0, cursorY);
      cursorY += lineHeight;
    }
  }

  ctx.restore();
}
